import { useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { AnimatePresence } from "framer-motion"
import APIService from "@/util/api-service";
import SessionStorageService from '@/util/session-storage-service';
import useWills from "@/lib/useWills";
import useNotification from "@/lib/useNotification";
import { STKContainer, STKRow, STKCol } from '@/components/common/grid';
import STKConfirmationBox from '@/components/common/confirmation-box';
import STKButton from '@/components/common/button';
import STKIcon from '@/components/common/icon';
import STKAnimated from '@/components/common/animated';
import STKPdfListItem from '@/components/common/pdf-list-item';
import { formatDate } from "@/helpers/format-helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { User } from '@/lib/useUser';
import type { Will, WillTypes } from '@/types';
import css from "./styles.module.scss";
import { PageContentField } from '@/providers/content-provider';

type Props = {
    user: User | null
    willType: WillTypes
}

const MainView = ( props: { wills: Will[], gettingWills: boolean, setMaybeDelete: (will: Will) => void } ) => {
    const { wills, gettingWills, setMaybeDelete } = props;
    return(
        <div>
            {
                gettingWills ? 
                <FontAwesomeIcon icon={["fas", "spinner"]} spinPulse size="2x" />
                :
                <STKAnimated exit={{ y:"-20px", opacity: 0 }} type="down-to-up">
                    <ul className={css.willList}>
                        {wills?.map( ( will:Will ) => {
                            return (
                                
                                <li key={will.id}>
                                    <STKPdfListItem name={will.fields?.name} time={will.generationTime} onClick={ () => { setMaybeDelete( will ) } }>
                                        <button><FontAwesomeIcon icon={["far", "xmark"]} />Poista PDF</button>
                                    </STKPdfListItem>
                                </li>
                            ); 
                        })}
                    </ul>
                </STKAnimated>
            }
        </div>
    );
}

const ConfirmationView = ( props: { maybeDelete: Will | null, deleteWill: () => void, setMaybeDelete: (will: Will | null) => void }) => {
    const { maybeDelete, deleteWill, setMaybeDelete } = props;
    return (
        <STKConfirmationBox>
            <h6 className="mb-4">Oletko varma, että haluat poistaa:<br></br>{maybeDelete?.fields?.name} - {formatDate(maybeDelete?.generationTime)}</h6>
            <STKContainer>
                <STKRow gutterX={5} gutterY={2}>
                    <STKCol>
                        <STKButton onClick={deleteWill}>Kyllä</STKButton>
                    </STKCol>
                    <STKCol>
                        <STKButton alt onClick={() => { setMaybeDelete(null) }}>Ei</STKButton>
                    </STKCol>
                </STKRow>
            </STKContainer>
        </STKConfirmationBox>
    );
}

const WillManager = ( props: Props ) => {

    const { wills, setWills, gettingWills } = useWills(props.user, props.willType);
    const { addNotification, Notification } = useNotification();

    const [ maybeDelete, setMaybeDelete ] = useState<Will|null>(null);


    const deleteWill = () => {
        if ( maybeDelete ) {
            setMaybeDelete(null);

            const willSession = SessionStorageService.getWill(props.willType);
            if ( willSession ) {
                if ( willSession.id == maybeDelete.id ) {
                    SessionStorageService.removeWill(props.willType)
                }
            } 

            APIService.postWithUidToken( `/api/hautajaistestamentti/delete/${props.willType.toLowerCase()}`, maybeDelete.id ) 
            .then( ( data ) => {
                if ( ! data.error ) {
                    let willsState = [...wills];
                    willsState = willsState.filter( ( will ) => {
                        return will.id !== maybeDelete.id;
                    });
                    setWills(willsState); 
                } else {
                    addNotification('ERROR');
                }
                
            })
            .catch( ( error ) => {
                captureException(error);
                addNotification('ERROR');
            })
        }
        
    }


    return(
        <div>
            <>
                <Notification />
                <div className={css.willManager__main}>
                    <PageContentField section={'main'} index={0} />
                    <AnimatePresence>
                        { maybeDelete ?
                            <STKAnimated exit={{ y:"-20px", opacity: 0 }} type="down-to-up">
                                <ConfirmationView maybeDelete={maybeDelete} setMaybeDelete={setMaybeDelete} deleteWill={deleteWill} />
                            </STKAnimated>
                            :
                            
                            <MainView wills={wills} gettingWills={gettingWills} setMaybeDelete={setMaybeDelete} />  
                        }
                    </AnimatePresence>
                </div>
            </>
        </div>
    );

}

export default WillManager;